import { supabase } from '@/features/sistema/services/supabaseClient';

import {
  hasUserLikedComment,
  likeComment,
  listLikesByComments,
  unlikeComment,
} from './commentLikesApi';
import {
  type AppCommentRecord,
  createComment,
  listCommentsByThread,
} from './commentsApi';
import {
  type CommentThreadLatestViewRecord,
  ensureThread as ensureThreadForResource,
} from './commentThreadsApi';
import {
  deleteThreadRead as deleteThreadReadApi,
  markThreadRead as markThreadReadApi,
} from './threadReadsApi';
import {
  getUnreadCountByObra as getUnreadCountByObraApi,
  getUnreadMentionCountByObra as getUnreadMentionCountByObraApi,
} from './unreadApi';

export type CommentRow = AppCommentRecord;

export type ThreadRow = CommentThreadLatestViewRecord & {
  thread_id: string;
  last_comment_at: string | null;
  last_comment_by: string | null;
  recurso_label?: string | null;
  recurso_url?: string | null;
  is_unread?: boolean | null;
};

type ThreadListRow = {
  id: string;
  obra_id: string | null;
  recurso: string | null;
  recurso_id: string | null;
  created_at: string;
  created_by: string | null;
  last_at: string;
  last_comment_id: string | null;
  last_comment_conteudo: string | null;
  last_comment_tipo: string | null;
  last_comment_mencoes: string[] | null;
  last_comment_created_at: string | null;
  last_comment_created_by: string | null;
  total_comments: number;
  is_unread: boolean | null;
  has_mention: boolean | null;
  recurso_label?: string | null;
  recurso_url?: string | null;
};

type FetchThreadsParams = {
  obraId: string;
  search?: string | null;
  onlyUnread?: boolean;
  onlyMentions?: boolean;
  limit?: number;
  offset?: number;
};

export async function fetchThreadsByObra(params: FetchThreadsParams): Promise<ThreadRow[]> {
  const { obraId } = params;

  if (!obraId) {
    return [];
  }

  const {
    data,
    error,
  } = await supabase.rpc('fn_app_threads_list', {
    p_obra_id: obraId,
    p_search: params.search ?? null,
    p_only_mentions: params.onlyMentions ?? false,
    p_limit: params.limit ?? 50,
    p_offset: params.offset ?? 0,
  });

  if (error) {
    throw error;
  }

  const rows = (data ?? []) as ThreadListRow[];
  return rows.map((row) =>
    mapThreadRecord({
      ...row,
      unread: row.is_unread ?? (row as Partial<CommentThreadLatestViewRecord>).unread ?? null,
      last_comment_at: row.last_comment_created_at,
      last_comment_by: row.last_comment_created_by,
    }),
  );
}

export async function fetchThreadComments(
  threadId: string,
  limit = 100,
  offset = 0,
): Promise<CommentRow[]> {
  const rows = await listCommentsByThread(threadId, { limit, offset });
  return rows;
}

export async function ensureThread(params: {
  obraId?: string | null;
  recurso: string;
  recursoId: string;
}) {
  return ensureThreadForResource(params);
}

type SendThreadCommentArgs = {
  threadId: string;
  conteudo: string;
  tipo?: string;
  arquivo_url?: string | null;
  arquivoUrl?: string | null;
  arquivo_nome?: string | null;
  arquivoNome?: string | null;
  mencoes?: string[] | null;
  comentario_pai_id?: string | null;
  parentId?: string | null;
  createdBy?: string | null;
  created_by?: string | null;
};

export async function sendThreadComment(args: SendThreadCommentArgs): Promise<CommentRow> {
  const created = await createComment({
    threadId: args.threadId,
    conteudo: args.conteudo,
    tipo: args.tipo,
    arquivoUrl: args.arquivoUrl ?? args.arquivo_url ?? null,
    arquivoNome: args.arquivoNome ?? args.arquivo_nome ?? null,
    mencoes: args.mencoes ?? null,
    parentId: args.parentId ?? args.comentario_pai_id ?? null,
    createdBy: args.createdBy ?? args.created_by ?? null,
  });

  return created;
}

export async function markThreadRead(threadId: string): Promise<void> {
  await markThreadReadApi(threadId);
}

export async function markThreadUnread(threadId: string, userId?: string | null): Promise<void> {
  await deleteThreadReadApi(threadId, userId ?? undefined);
}

export async function getCommentLikes(commentIds: string[]) {
  return listLikesByComments(commentIds);
}

export async function toggleCommentLike(commentId: string, usuarioId: string, shouldLike: boolean) {
  if (shouldLike) {
    await likeComment(commentId, usuarioId);
  } else {
    await unlikeComment(commentId, usuarioId);
  }
}

export async function hasUserLiked(commentId: string, usuarioId: string) {
  return hasUserLikedComment(commentId, usuarioId);
}

export async function markAllThreadsRead(params: {
  obraId?: string | null;
  onlyMentions?: boolean;
} = {}): Promise<void> {
  const { obraId = null, onlyMentions = false } = params;

  const { error } = await supabase.rpc('fn_app_mark_all_threads_read', {
    p_obra_id: obraId,
    p_only_mentions: onlyMentions,
  });

  if (error) {
    throw error;
  }
}

export async function getPreferredObras(): Promise<string[]> {
  return []
}

export async function setPreferredObras(obraIds: string[]): Promise<void> {
  // preferências persistem apenas localmente atualmente
  void obraIds
}

export async function getUnreadByObra(obraId: string, usuarioId?: string | null): Promise<number> {
  if (!obraId) return 0;
  const user = usuarioId && usuarioId.trim() ? usuarioId : null;
  return getUnreadCountByObraApi(obraId, user);
}

export async function getUnreadMentionCountByObra(obraId: string, usuarioId: string): Promise<number> {
  return getUnreadMentionCountByObraApi(obraId, usuarioId);
}

function mapThreadRecord(
  row: CommentThreadLatestViewRecord & {
    last_comment_at?: string | null;
    last_comment_by?: string | null;
    recurso_label?: string | null;
    recurso_url?: string | null;
    is_unread?: boolean | null;
  },
): ThreadRow {
  return {
    ...row,
    thread_id: row.id,
    last_comment_at: row.last_comment_at ?? null,
    last_comment_by: row.last_comment_by ?? null,
    recurso_label: row.recurso_label ?? null,
    recurso_url: row.recurso_url ?? null,
    is_unread: row.is_unread ?? (row as unknown as { unread?: boolean }).unread ?? null,
    last_created_by:
      (row as ThreadRow).last_created_by ??
      row.last_comment_by ??
      null,
    last_snippet: (row as ThreadRow).last_snippet ?? null,
  };
}

